/**
 * Lead-Detail mit:
 * - Stamm-Daten + alle Form-Daten
 * - Status-Pipeline-Buttons (neu → geprueft → versendet → abgeschlossen)
 * - Mail-Editor-Modal (POST /api/leads/:id/send-mail)
 * - Edit-Inline für name/firma/email/etc
 * - Visitenkarte als Original-Bild (kein Lightbox-only · Sprint 10)
 * - Anhänge-Verwaltung (Sprint 10): Plan · Foto · PDF · Visitenkarte
 * - KI-Strukturierung (Sprint 10): Magic-Persistent aus data._magic
 */
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FilePlus2, Sparkles, Trash2 } from 'lucide-react';
import { AppHeader } from '../components/AppHeader';
import { AssigneePicker } from '../components/AssigneePicker';
import { CardLightbox } from '../components/CardLightbox';
import { api, getAccessToken } from '../lib/api';
import { useAuth } from '../lib/auth';
import { FormDefinition, FormField } from '../lib/forms';
import { applyVariables, leadToTemplateVars } from '../lib/templateVars';

interface MagicSummary {
  structured?: {
    summary?: string;
    key_points?: string[];
    next_steps?: string[];
    sentiment?: string;
  };
  raw_text?: string;
  target_field?: string | null;
  model?: string;
  created_at?: string;
}

interface Lead {
  id: string;
  tenant_id: string;
  form_id: string;
  form_version: number;
  status: string;
  name: string | null;
  firma: string | null;
  email: string | null;
  telefon: string | null;
  /** Sprint 63 · Mobilfunknummer als Top-Level-Spalte */
  mobil: string | null;
  potenzial: number | null;
  messetag: string | null;
  data: Record<string, unknown>;
  erfasser_id: string;
  erfasser_name: string | null;
  uebernehmender_id: string | null;
  uebernehmender_name: string | null;
  visitenkarte_url: string | null;
  /** Sprint 57 (Bug #1) · HMAC-signed URL fuer <img> ohne Bearer-Header.
   *  Backend setzt das · 1h TTL · null falls keine Visitenkarte. */
  visitenkarte_signed_url: string | null;
  created_at: string;
  sent_at: string | null;
  closed_at: string | null;
  magic_summary: MagicSummary | null;
}

interface LeadAttachment {
  id: string;
  lead_id: string;
  kind: 'visitenkarte' | 'sketch' | 'foto' | 'dokument';
  filename: string;
  mime: string;
  size: number;
  uploaded_by: string | null;
  created_at: string;
  url: string;
  /** Sprint 49 · signed-URL · für <img>/<a> ohne Bearer-Header */
  signed_url?: string | null;
}

const ATTACH_MIMES = 'image/jpeg,image/png,image/webp,application/pdf';
const ATTACH_KIND_LABELS: Record<LeadAttachment['kind'], string> = {
  visitenkarte: 'Visitenkarte',
  sketch: 'Planzeichnung',
  foto: 'Foto',
  dokument: 'Dokument',
};

const STATUS_FLOW = ['neu', 'geprueft', 'versendet', 'abgeschlossen'];
const STATUS_LABELS: Record<string, string> = {
  neu: 'Neu',
  geprueft: 'Versandbereit',
  versendet: 'Versendet',
  abgeschlossen: 'Abgeschlossen',
  verworfen: 'Verworfen',
};

export function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [showMailModal, setShowMailModal] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [attachments, setAttachments] = useState<LeadAttachment[]>([]);
  const [attachUploading, setAttachUploading] = useState(false);
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const attachInputRef = useRef<HTMLInputElement | null>(null);
  // Sprint 19 (S19.4) · Re-Magic mit Anhängen
  const [remagicBusy, setRemagicBusy] = useState(false);
  const [remagicError, setRemagicError] = useState<string | null>(null);

  // edit-state
  const [editName, setEditName] = useState('');
  const [editFirma, setEditFirma] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editTelefon, setEditTelefon] = useState('');
  // Sprint 63 · mobil als Top-Level-Edit-State
  const [editMobil, setEditMobil] = useState('');
  const [editPotenzial, setEditPotenzial] = useState<number | ''>('');
  // Sprint 64 · Übernehmender direkt im Stamm editierbar
  const [editUebernehmenderId, setEditUebernehmenderId] = useState('');

  // Sprint 73 · Notiz inline-edit pro Feld-Key
  const [editingNoteKey, setEditingNoteKey] = useState<string | null>(null);
  const [editNoteValue, setEditNoteValue] = useState('');
  const [noteSaveBusy, setNoteSaveBusy] = useState(false);
  const [noteHistoryVersion, setNoteHistoryVersion] = useState(0);

  async function reload() {
    if (!id) return;
    try {
      const l = await api<Lead>(`/api/leads/${id}`);
      setLead(l);
      setEditName(l.name ?? '');
      setEditFirma(l.firma ?? '');
      setEditEmail(l.email ?? '');
      setEditTelefon(l.telefon ?? '');
      setEditMobil(l.mobil ?? '');
      setEditUebernehmenderId(l.uebernehmender_id ?? '');
      setEditPotenzial(l.potenzial ?? '');
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Lead nicht gefunden');
    }
  }

  async function reloadAttachments() {
    if (!id) return;
    try {
      const list = await api<LeadAttachment[]>(`/api/leads/${id}/attachments`);
      setAttachments(list);
    } catch {
      // Attachments-Fail ignoriert — Lead-Detail bleibt nutzbar
    }
  }

  async function uploadAttachments(e: ChangeEvent<HTMLInputElement>) {
    if (!id) return;
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setAttachUploading(true);
    setError(null);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        fd.append(
          'kind',
          f.type.startsWith('image/') ? 'foto' : 'dokument',
        );
        const resp = await fetch(`/api/leads/${id}/attachments`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
          body: fd,
        });
        if (!resp.ok) {
          const errBody = await resp.json().catch(() => ({}));
          setError((errBody as { detail?: string }).detail ?? `Upload fehlgeschlagen (${resp.status})`);
        }
      }
      await reloadAttachments();
      setSuccess('Anhang/Anhänge hochgeladen');
    } catch (err) {
      setError((err as Error).message ?? 'Upload-Fehler');
    } finally {
      setAttachUploading(false);
      if (attachInputRef.current) attachInputRef.current.value = '';
    }
  }

  // Sprint 19 (S19.4) · Re-Magic mit allen Bild-Anhängen.
  async function rerunMagicWithAttachments() {
    if (!lead) return;
    const imageAtts = attachments.filter((a) => a.mime.startsWith('image/')).slice(0, 4);
    const rawText =
      lead.magic_summary?.raw_text ||
      (typeof lead.data?.notiz === 'string' ? (lead.data.notiz as string) : '');
    if (!rawText || !rawText.trim()) {
      setRemagicError('Keine Notiz vorhanden — erst eine Notiz erfassen.');
      return;
    }
    if (imageAtts.length === 0) {
      setRemagicError('Keine Bild-Anhänge gefunden.');
      return;
    }
    const ok = window.confirm(
      `${imageAtts.length} Bild${imageAtts.length === 1 ? '' : 'er'} mit auswerten? Vision-Modell kostet zusätzliche Token.`,
    );
    if (!ok) return;
    setRemagicBusy(true);
    setRemagicError(null);
    try {
      const resp = await api<{ structured: unknown; vision_used?: boolean }>(
        '/api/text/magic',
        {
          method: 'POST',
          body: {
            raw_text: rawText,
            target_field: 'notiz',
            lead_id: lead.id,
            attachment_ids: imageAtts.map((a) => a.id),
          },
        },
      );
      if (resp.vision_used === false) {
        setRemagicError(
          'Magic ohne Vision durchgelaufen — Bilder konnten nicht verarbeitet werden.',
        );
      }
      await reload();
      setSuccess('KI-Strukturierung mit Anhängen aktualisiert');
    } catch (err) {
      setRemagicError((err as { detail?: string }).detail ?? 'Re-Magic fehlgeschlagen');
    } finally {
      setRemagicBusy(false);
    }
  }

  async function deleteAttachment(att: LeadAttachment) {
    if (!id) return;
    if (!confirm(`Anhang "${att.filename}" löschen?`)) return;
    try {
      await api(`/api/leads/${id}/attachments/${att.id}`, { method: 'DELETE' });
      await reloadAttachments();
      setSuccess(`"${att.filename}" gelöscht`);
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Löschen fehlgeschlagen');
    }
  }

  useEffect(() => {
    void reload();
    void reloadAttachments();
  }, [id]);

  // Sprint 53 · Form-Definition laden fuer Label-Lookup in Form-Daten-Section
  useEffect(() => {
    if (!lead?.form_id) return;
    let cancelled = false;
    api<FormDefinition>(`/api/forms/${lead.form_id}`)
      .then((f) => { if (!cancelled) setFormDef(f); })
      .catch(() => { /* ignore — fallback auf raw keys */ });
    return () => { cancelled = true; };
  }, [lead?.form_id]);

  // Sprint 73 · Notiz inline speichern (PATCH /api/leads/:id mit gemergtem data-Blob).
  async function saveNote(noteKey: string): Promise<void> {
    if (!lead) return;
    setNoteSaveBusy(true);
    setError(null);
    try {
      const currentData = (lead.data as Record<string, unknown> | null) ?? {};
      const nextData = { ...currentData, [noteKey]: editNoteValue };
      await api(`/api/leads/${lead.id}`, {
        method: 'PATCH',
        body: { data: nextData },
      });
      await reload();
      setNoteHistoryVersion((v) => v + 1);
      setEditingNoteKey(null);
      setEditNoteValue('');
      setSuccess('Notiz aktualisiert');
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Fehler beim Speichern');
    } finally {
      setNoteSaveBusy(false);
    }
  }

  async function setStatus(newStatus: string) {
    if (!lead) return;
    setError(null);
    try {
      await api(`/api/leads/${lead.id}`, {
        method: 'PATCH',
        body: { status: newStatus },
      });
      await reload();
      setSuccess(`Status auf "${STATUS_LABELS[newStatus] ?? newStatus}" gesetzt`);
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Fehler');
    }
  }

  async function saveEdits(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!lead) return;
    setError(null);
    try {
      await api(`/api/leads/${lead.id}`, {
        method: 'PATCH',
        body: {
          name: editName || null,
          firma: editFirma || null,
          email: editEmail || null,
          telefon: editTelefon || null,
          mobil: editMobil || null,
          uebernehmender_id: editUebernehmenderId || null,
          potenzial: editPotenzial === '' ? null : Number(editPotenzial),
        },
      });
      await reload();
      setEditing(false);
      setSuccess('Lead aktualisiert');
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Speichern fehlgeschlagen');
    }
  }

  if (!user) return null;
  if (error && !lead) {
    return (
      <main className="app-shell">
        <AppHeader subtitle="LEAD" backTo="/leads" />
        <section className="app-shell__main">
          <div className="app-shell__card">
            <p className="auth-error">{error}</p>
            <button className="auth-submit" onClick={() => navigate('/leads')}>
              ZURÜCK ZUR ÜBERSICHT
            </button>
          </div>
        </section>
      </main>
    );
  }
  if (!lead) {
    return (
      <main className="app-shell">
        <AppHeader subtitle="LEAD" backTo="/leads" />
        <section className="app-shell__main">
          <p className="app-shell__card-meta">LÄDT…</p>
        </section>
      </main>
    );
  }

  const canSendMail = Boolean(
    lead.email &&
      (user.role === 'super_admin' ||
        user.role === 'operator_admin' ||
        user.role === 'dispatcher'),
  );
  // Sprint 73 · Erfasser darf Mail vorbereiten (Subject+Body speichern + Status=versandbereit),
  // aber nicht senden. Admin/Dispatcher: kann beides.
  const canPrepareMail = Boolean(lead.email && (canSendMail || user.role === 'employee'));
  // Im Mail-Modal: SENDEN-Button nur fuer canSendMail, VERSANDBEREIT-Button fuer alle anderen.
  const isMailPreparerOnly = canPrepareMail && !canSendMail;
  const isAlreadySent = lead.status === 'versendet';

  return (
    <main className="app-shell">
      <AppHeader subtitle="LEAD" />
      <section className="app-shell__main">
        <div className="app-shell__card lead-detail-card">
          <div className="lead-detail-header">
            <button type="button" className="back-button" onClick={() => navigate('/leads')}>
              ← Übersicht
            </button>
            <span className={`status-pill status-pill--${lead.status}`}>
              {STATUS_LABELS[lead.status] ?? lead.status}
            </span>
          </div>

          <h1 className="app-shell__card-title">{lead.name || 'Unbenannter Lead'}</h1>
          {lead.firma ? <p className="app-shell__card-meta">{lead.firma}</p> : null}

          {success ? <p className="auth-success" role="status">{success}</p> : null}
          {error ? <p className="auth-error" role="alert">{error}</p> : null}

          {/* Status-Pipeline */}
          <div className="status-pipeline">
            {STATUS_FLOW.map((s) => (
              <button
                key={s}
                type="button"
                className={`status-pipeline__step ${lead.status === s ? 'status-pipeline__step--active' : ''}`}
                onClick={() => setStatus(s)}
                disabled={lead.status === s}
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>

          {/* Visitenkarte als ORIGINAL · Sprint 10: groß sichtbar, Klick öffnet Lightbox
              Sprint 57 (Bug #1) · signed_url fuer <img> ohne Bearer-Header */}
          {lead.visitenkarte_url ? (
            <div className="lead-detail-card-section">
              <h2 className="profile-section-title">Visitenkarte · Original</h2>
              <div className="lead-detail-card-original">
                <img
                  src={lead.visitenkarte_signed_url ?? lead.visitenkarte_url}
                  alt="Visitenkarte (Original)"
                  className="lead-detail-card-original-img"
                  loading="lazy"
                  decoding="async"
                  onClick={() => setShowLightbox(true)}
                />
                <button
                  type="button"
                  className="app-shell__logout"
                  onClick={() => setShowLightbox(true)}
                >
                  IN VOLLBILD ÖFFNEN
                </button>
              </div>
              {showLightbox ? (
                <CardLightbox
                  src={lead.visitenkarte_signed_url ?? lead.visitenkarte_url}
                  alt={`Visitenkarte von ${lead.name ?? 'Lead'}`}
                  onClose={() => setShowLightbox(false)}
                />
              ) : null}
            </div>
          ) : null}

          {/* Sprint 53 · Notiz wird unten in der Form-Daten-Section als
              gestyltes Notizen-Feld gerendert (siehe FormDataField-Loop). */}

          {/* Sprint 10 · Anhänge: Plan · Foto · PDF · Visitenkarten-Originale
              Sprint 19 (S19.4) · direkt unter Notiz, mit Re-Magic-Button */}
          <div className="lead-detail-card-section">
            <div className="lead-detail-section-header">
              <h2 className="profile-section-title">Anhänge</h2>
              <input
                ref={attachInputRef}
                type="file"
                accept={ATTACH_MIMES}
                multiple
                onChange={uploadAttachments}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="app-shell__logout"
                onClick={() => attachInputRef.current?.click()}
                disabled={attachUploading}
              >
                <FilePlus2 size={14} /> {attachUploading ? 'LADE…' : 'HINZUFÜGEN'}
              </button>
            </div>
            {(() => {
              // Sprint 53 · Visitenkarte als virtuelles Attachment in der Liste
              // (falls noch nicht als echtes lead_attachment vorhanden).
              const hasRealVk = attachments.some((a) => a.kind === 'visitenkarte');
              const virtualVk: LeadAttachment | null = (lead.visitenkarte_url && !hasRealVk)
                ? {
                    id: '__visitenkarte__',
                    lead_id: lead.id,
                    kind: 'visitenkarte',
                    filename: 'Visitenkarte.jpg',
                    mime: 'image/jpeg',
                    size: 0,
                    uploaded_by: null,
                    created_at: lead.created_at,
                    url: lead.visitenkarte_url,
                    // Sprint 57 (Bug #1) · signed_url statt auth-required url
                    signed_url: lead.visitenkarte_signed_url ?? lead.visitenkarte_url,
                  }
                : null;
              const merged = virtualVk ? [virtualVk, ...attachments] : attachments;
              if (merged.length === 0) {
                return (
                  <p className="form-field__hint">
                    Keine Anhänge · Planzeichnung · Foto · PDF hochladen
                  </p>
                );
              }
              return (
                <ul className="lead-attachments-list">
                  {merged.map((att) => {
                    // Sprint 49 · signed-URL für <img>/<a> · Browser sendet keinen Bearer
                    const fileHref = att.signed_url ?? att.url;
                    const isVirtual = att.id === '__visitenkarte__';
                    return (
                      <AttachmentItemView
                        key={att.id}
                        att={att}
                        fileHref={fileHref}
                        isVirtual={isVirtual}
                        onDelete={() => void deleteAttachment(att)}
                      />
                    );
                  })}
                </ul>
              );
            })()}
          </div>

          {/* Sprint 10 · KI-Strukturierung (persistent aus data._magic) */}
          {lead.magic_summary?.structured ? (
            <div className="lead-detail-card-section">
              <div className="lead-detail-section-header">
                <h2 className="profile-section-title">
                  <Sparkles size={16} style={{ display: 'inline-block', marginRight: 6, verticalAlign: 'text-bottom' }} />
                  KI-Strukturierung
                </h2>
                {lead.magic_summary.structured.sentiment ? (
                  <span
                    className={`status-pill status-pill--${
                      lead.magic_summary.structured.sentiment === 'positiv'
                        ? 'versendet'
                        : lead.magic_summary.structured.sentiment === 'ablehnend'
                          ? 'verworfen'
                          : 'neu'
                    }`}
                  >
                    {lead.magic_summary.structured.sentiment.toUpperCase()}
                  </span>
                ) : null}
              </div>
              {lead.magic_summary.structured.summary ? (
                <p className="lead-magic-summary">
                  {lead.magic_summary.structured.summary}
                </p>
              ) : null}
              {Array.isArray(lead.magic_summary.structured.key_points) &&
              lead.magic_summary.structured.key_points.length > 0 ? (
                <>
                  <h3 className="lead-magic-subhead">Kernpunkte</h3>
                  <ul className="lead-magic-bullets">
                    {lead.magic_summary.structured.key_points.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </>
              ) : null}
              {Array.isArray(lead.magic_summary.structured.next_steps) &&
              lead.magic_summary.structured.next_steps.length > 0 ? (
                <>
                  <h3 className="lead-magic-subhead">Nächste Schritte</h3>
                  <ul className="lead-magic-bullets lead-magic-bullets--steps">
                    {lead.magic_summary.structured.next_steps.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </>
              ) : null}
              {lead.magic_summary.created_at ? (
                <p className="form-field__hint" style={{ marginTop: 12 }}>
                  Generiert {new Date(lead.magic_summary.created_at).toLocaleString('de-DE')}
                  {lead.magic_summary.model ? ` · ${lead.magic_summary.model}` : ''}
                </p>
              ) : null}
              {/* Sprint 19 (S19.4) · Re-Magic mit Anhängen */}
              {attachments.some((a) => a.mime.startsWith('image/')) ? (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button
                    type="button"
                    className="app-shell__logout"
                    onClick={() => void rerunMagicWithAttachments()}
                    disabled={remagicBusy}
                  >
                    <Sparkles size={14} />
                    {remagicBusy ? ' RE-MAGIC LÄUFT…' : ' RE-MAGIC MIT ANHÄNGEN'}
                  </button>
                  {remagicError ? <p className="auth-error">{remagicError}</p> : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Stamm-Daten */}
          <div className="lead-detail-card-section">
            <div className="lead-detail-section-header">
              <h2 className="profile-section-title">Stamm-Daten</h2>
              {!editing ? (
                <button type="button" className="app-shell__logout" onClick={() => setEditing(true)}>
                  BEARBEITEN
                </button>
              ) : null}
            </div>
            {!editing ? (
              <dl className="lead-detail-fields">
                <Field label="Name" value={lead.name} />
                <Field label="Firma" value={lead.firma} />
                <Field label="E-Mail" value={lead.email} mono />
                <Field label="Festnetz" value={lead.telefon} mono />
                <Field label="Mobilfunk" value={lead.mobil} mono />
                {/* Sprint 64 · Übernehmender direkt in Stammdaten */}
                <Field label="Übernehmender" value={lead.uebernehmender_name} />
                <Field label="Potenzial" value={lead.potenzial !== null ? `${lead.potenzial}/10` : null} />
                <Field label="Messetag" value={lead.messetag} mono />
                <Field label="Erfasst" value={new Date(lead.created_at).toLocaleString('de-DE')} mono />
                {lead.sent_at ? (
                  <Field label="Versendet" value={new Date(lead.sent_at).toLocaleString('de-DE')} mono />
                ) : null}
              </dl>
            ) : (
              <form onSubmit={saveEdits} className="auth-form">
                <div className="auth-field">
                  <label className="auth-field__label">NAME</label>
                  <input className="auth-input" value={editName} onChange={(e) => setEditName(e.target.value)} />
                </div>
                <div className="auth-field">
                  <label className="auth-field__label">FIRMA</label>
                  <input className="auth-input" value={editFirma} onChange={(e) => setEditFirma(e.target.value)} />
                </div>
                <div className="auth-field">
                  <label className="auth-field__label">E-MAIL</label>
                  <input type="email" className="auth-input" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                </div>
                <div className="auth-field">
                  <label className="auth-field__label">FESTNETZ</label>
                  <input className="auth-input" value={editTelefon} onChange={(e) => setEditTelefon(e.target.value)} />
                </div>
                <div className="auth-field">
                  <label className="auth-field__label">MOBILFUNK</label>
                  <input className="auth-input" value={editMobil} onChange={(e) => setEditMobil(e.target.value)} />
                </div>
                {/* Sprint 64 · Übernehmender editierbar im Stamm-Edit */}
                <div className="auth-field">
                  <label className="auth-field__label">ÜBERNEHMENDER</label>
                  <AssigneePicker
                    value={editUebernehmenderId}
                    onChange={(id) => setEditUebernehmenderId(id)}
                    placeholder="Name suchen…"
                  />
                </div>
                <div className="auth-field">
                  <label className="auth-field__label">POTENZIAL (1-10)</label>
                  <input type="number" min={1} max={10} className="auth-input" value={editPotenzial} onChange={(e) => setEditPotenzial(e.target.value === '' ? '' : Math.min(10, Math.max(1, Number(e.target.value))))} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" className="auth-submit">SPEICHERN</button>
                  <button type="button" className="app-shell__logout" onClick={() => { setEditing(false); void reload(); }}>ABBRECHEN</button>
                </div>
              </form>
            )}
          </div>

          {/* Sprint 53 · Form-Daten mit Labels statt technischen Keys
                Notizen-Felder werden separat unten als Notiz-Box gerendert */}
          {(() => {
            const entries = Object.entries(lead.data ?? {})
              .filter(([k]) => !k.startsWith('_'))
              .filter(([k]) => !['name', 'firma', 'email', 'telefon', 'mobil', 'potenzial', 'messetag', 'uebernehmender'].includes(k));
            const fieldById = new Map<string, FormField>();
            (formDef?.schema?.fields ?? []).forEach((f) => fieldById.set(f.id, f));
            // Notiz/textarea/notes-Felder filtern (separat dargestellt)
            const noteEntries: Array<[string, unknown, FormField | null]> = [];
            const otherEntries: Array<[string, unknown, FormField | null]> = [];
            for (const [k, v] of entries) {
              const field = fieldById.get(k) ?? null;
              const isNote = field?.type === 'textarea' || k === 'notiz' || k === 'notizen' || k === 'notes';
              if (isNote && typeof v === 'string' && v.trim()) {
                noteEntries.push([k, v, field]);
              } else if (!isNote) {
                otherEntries.push([k, v, field]);
              }
            }
            return (
              <>
                {otherEntries.length > 0 ? (
                  <div className="lead-detail-card-section">
                    <h2 className="profile-section-title">Form-Daten</h2>
                    <dl className="lead-detail-fields">
                      {otherEntries.map(([k, v, field]) => (
                        <FormDataField
                          key={k}
                          field={field}
                          value={v}
                          assigneeName={lead.uebernehmender_name}
                        />
                      ))}
                    </dl>
                  </div>
                ) : null}
                {noteEntries.length > 0 ? (
                  <div className="lead-detail-card-section">
                    <h2 className="profile-section-title">Notizen</h2>
                    {noteEntries.map(([k, v, field]) => {
                      const isEditing = editingNoteKey === k;
                      return (
                        <div key={k} className="lead-detail-note-box">
                          <div className="lead-detail-note-head">
                            {field?.label ? (
                              <div className="lead-detail-note-label">{field.label.toUpperCase()}</div>
                            ) : <div className="lead-detail-note-label">NOTIZ</div>}
                            {!isEditing ? (
                              <button
                                type="button"
                                className="lead-detail-note-edit-btn"
                                onClick={() => {
                                  setEditingNoteKey(k);
                                  setEditNoteValue(String(v));
                                }}
                              >
                                BEARBEITEN
                              </button>
                            ) : null}
                          </div>
                          {isEditing ? (
                            <>
                              <textarea
                                className="lead-detail-note-textarea"
                                value={editNoteValue}
                                onChange={(e) => setEditNoteValue(e.target.value)}
                                rows={6}
                                disabled={noteSaveBusy}
                              />
                              <div className="lead-detail-note-edit-actions">
                                <button
                                  type="button"
                                  className="auth-submit"
                                  onClick={() => void saveNote(k)}
                                  disabled={noteSaveBusy}
                                >
                                  {noteSaveBusy ? 'SPEICHERT…' : 'SPEICHERN'}
                                </button>
                                <button
                                  type="button"
                                  className="app-shell__logout"
                                  onClick={() => {
                                    setEditingNoteKey(null);
                                    setEditNoteValue('');
                                  }}
                                  disabled={noteSaveBusy}
                                >
                                  ABBRECHEN
                                </button>
                              </div>
                            </>
                          ) : (
                            <div className="lead-detail-note-body">{String(v)}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </>
            );
          })()}

          {/* Mail-Send */}
          <div className="lead-detail-card-section">
            <h2 className="profile-section-title">Antwort-Mail</h2>
            {!lead.email ? (
              <p className="form-field__hint">Lead hat keine E-Mail-Adresse — Antwort-Mail nicht möglich.</p>
            ) : (
              <>
                <p className="form-field__hint">
                  An: <strong>{lead.email}</strong>
                  {isAlreadySent ? ' · bereits versendet' : ''}
                </p>
                <button
                  type="button"
                  className="auth-submit"
                  disabled={!canPrepareMail || isAlreadySent}
                  onClick={() => setShowMailModal(true)}
                >
                  {isAlreadySent
                    ? 'BEREITS GESENDET'
                    : isMailPreparerOnly
                      ? '✉  ANTWORT-MAIL VORBEREITEN'
                      : '✉  ANTWORT-MAIL SENDEN'}
                </button>
              </>
            )}
          </div>

          {/* Sprint 23 · Versendete Mails (Subject + Body sichtbar) */}
          <MailHistorySection leadId={lead.id} />

          {/* Sprint 74 · gemeinsame Timeline · Status + Notiz-Aenderungen */}
          <HistorySection leadId={lead.id} refreshKey={noteHistoryVersion} />
        </div>

        {showMailModal && lead.email ? (
          <MailModal
            lead={lead}
            preparerOnly={isMailPreparerOnly}
            onClose={() => setShowMailModal(false)}
            onSent={async () => {
              setShowMailModal(false);
              await reload();
              setSuccess(
                isMailPreparerOnly
                  ? 'Antwort-Mail vorbereitet — Status auf "Versandbereit" gesetzt'
                  : 'Antwort-Mail gesendet — Status auf "versendet" gesetzt',
              );
            }}
          />
        ) : null}
      </section>
    </main>
  );
}

// Sprint 53 · Attachment-List-Item (extrahiert · stützt isVirtual fuer Visitenkarte ohne Delete)
function AttachmentItemView({
  att, fileHref, isVirtual, onDelete,
}: {
  att: LeadAttachment;
  fileHref: string;
  isVirtual: boolean;
  onDelete: () => void;
}) {
  return (
    <li className="lead-attachments-item">
      {att.mime.startsWith('image/') ? (
        <a
          href={fileHref}
          target="_blank"
          rel="noopener noreferrer"
          className="lead-attachments-thumb-link"
        >
          <img
            src={fileHref}
            alt={att.filename}
            className="lead-attachments-thumb"
            loading="lazy"
            decoding="async"
          />
        </a>
      ) : (
        <a
          href={fileHref}
          target="_blank"
          rel="noopener noreferrer"
          className="lead-attachments-thumb lead-attachments-thumb--pdf"
        >
          PDF
        </a>
      )}
      <div className="lead-attachments-meta">
        <span className="lead-attachments-name">{att.filename}</span>
        <span className="lead-attachments-info">
          {ATTACH_KIND_LABELS[att.kind]}
          {att.size > 0 ? ` · ${(att.size / 1024).toFixed(0)} KB` : ''}
          {' · '}{new Date(att.created_at).toLocaleDateString('de-DE')}
        </span>
      </div>
      {!isVirtual ? (
        <button
          type="button"
          className="lead-attachments-remove"
          onClick={onDelete}
          aria-label={`Anhang ${att.filename} löschen`}
        >
          <Trash2 size={14} />
        </button>
      ) : null}
    </li>
  );
}

function Field({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <>
      <dt className="lead-detail-fields__key">{label.toUpperCase()}</dt>
      <dd className={`lead-detail-fields__value ${mono ? 'admin-table__mono' : ''}`}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </dd>
    </>
  );
}

// Sprint 53 · Form-Daten-Render: Boolean → ✓/✗, Multiselect/Select → Labels statt values,
//                                  Assignee → Name statt UUID.
function FormDataField({
  field, value, assigneeName,
}: { field: FormField | null; value: unknown; assigneeName: string | null }) {
  const label = (field?.label ?? '').toUpperCase() || '—';
  const rendered = renderFormValue(field, value, assigneeName);
  return (
    <>
      <dt className="lead-detail-fields__key">{label}</dt>
      <dd className="lead-detail-fields__value">{rendered}</dd>
    </>
  );
}

function renderFormValue(
  field: FormField | null, raw: unknown, assigneeName: string | null,
): React.ReactNode {
  if (raw === null || raw === undefined || raw === '') return '—';
  const t = field?.type ?? '';
  if (t === 'assignee') {
    return assigneeName ?? (typeof raw === 'string' ? raw : JSON.stringify(raw));
  }
  if (t === 'boolean' || t === 'checkbox') {
    return raw === true || raw === 'true' || raw === 1 ? '✓' : '✗';
  }
  if (t === 'select' && field?.options && typeof raw === 'string') {
    const opt = field.options.find((o) => o.value === raw);
    return opt?.label ?? raw;
  }
  if (t === 'multiselect' && field?.options && Array.isArray(raw)) {
    const labels = (raw as unknown[])
      .map((v) => field.options?.find((o) => o.value === v)?.label ?? String(v))
      .filter(Boolean);
    return labels.length > 0 ? labels.join(' · ') : '—';
  }
  if (typeof raw === 'boolean') return raw ? '✓' : '✗';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number') return String(raw);
  if (Array.isArray(raw)) return raw.join(' · ');
  return JSON.stringify(raw);
}

interface MailTemplate {
  id: string;
  name: string;
  subject: string;
  body_text: string;
  is_default: boolean;
}

const FALLBACK_SUBJECT = 'Vielen Dank · UNITI 2026';

function buildFallbackBody(firstName: string): string {
  return `Hallo ${firstName},

vielen Dank für das Gespräch heute auf der UNITI 2026 in Stuttgart!

Wie besprochen melden wir uns in den nächsten Tagen mit weiteren Informationen zu unseren Geräten und Leistungen.

Bei Rückfragen jederzeit gerne: messe@tfc-germany.com

Beste Grüße,
Das LEADCraft-Team`;
}

// Sprint 58 · TemplateVars + applyVariables nach lib/templateVars.ts ausgelagert
// damit der Template-Editor (SettingsMailTemplates.tsx) sie nutzen kann.

function MailModal({
  lead,
  onClose,
  onSent,
  preparerOnly,
}: {
  lead: Lead;
  onClose: () => void;
  onSent: () => void | Promise<void>;
  // Sprint 73 · Erfasser-Modus: SENDEN-Button wird zu VERSANDBEREIT, PUT mail-draft
  preparerOnly?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // S06b Mail-Editor: Subject + Body editierbar, Variablen-Ersetzung clientseitig.
  // Sprint 57 (Bug #4a) · erweiterte Variablen aus Lead + Übernehmender.
  const templateVars = leadToTemplateVars(lead);
  const firstName = templateVars.first_name;
  // Sprint 73 · Wenn lead.data._mail_draft existiert, vorausfuellen
  const existingDraft = (lead.data as Record<string, unknown> | null)?._mail_draft as
    | { subject?: string; body?: string; prepared_at?: string; prepared_by?: string }
    | undefined;
  const hasDraft = Boolean(existingDraft && (existingDraft.subject || existingDraft.body));
  const [subject, setSubject] = useState(existingDraft?.subject || FALLBACK_SUBJECT);
  const [body, setBody] = useState(existingDraft?.body || buildFallbackBody(firstName));
  // Sprint 59 · showPreview-Toggle entfernt (war verwirrend, gleicher Inhalt
  // nur readonly vs editable). textarea ist immer editable.

  // Sprint 27 · 2-Phasen-Flow · Edit → Preview → Send
  // Sprint 74 · Wenn bereits ein Draft existiert, startet das Modal direkt in
  // Preview-Phase — Erfasser hat vorbereitet, Admin/Dispatcher sollen nicht
  // den ganzen Flow nochmal durchlaufen. Zurueck zum Edit bleibt moeglich.
  const [phase, setPhase] = useState<'edit' | 'preview'>(hasDraft ? 'preview' : 'edit');

  // Sprint 70 · FROM-Adresse fuer Mail-Vorschau (tenant-eigene oder default)
  const [fromEmail, setFromEmail] = useState<string>('messe@tfc-germany.com');
  useEffect(() => {
    api<{ from_email: string }>('/api/tenant/mail/from-display')
      .then((r) => setFromEmail(r.from_email))
      .catch(() => { /* fallback bleibt */ });
  }, []);

  // Sprint 57 (Feature #5) · MAGIC-Optimize-State
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeError, setOptimizeError] = useState<string | null>(null);
  const [optimizeBackup, setOptimizeBackup] = useState<{subject: string; body: string} | null>(null);

  async function runMailOptimize(): Promise<void> {
    if (optimizing) return;
    // Sprint 77 · Vorbereiteten Draft nur mit 2x Bestaetigung ueberschreiben.
    if (hasDraft) {
      const ok1 = window.confirm(
        'Es gibt bereits einen vorbereiteten Entwurf (Versandbereit). KI-Optimierung wirklich starten?',
      );
      if (!ok1) return;
      const ok2 = window.confirm(
        'Sicher? Der vorbereitete Text wird durch die KI-Vorschlaege ersetzt (RUECKGAENGIG-Button bleibt verfuegbar).',
      );
      if (!ok2) return;
    }
    setOptimizing(true);
    setOptimizeError(null);
    // Backup vor Optimize damit User mit "RÜCKGÄNGIG" zurueck kann
    const backup = { subject, body };
    try {
      const resp = await api<{ subject: string; body: string; eur_estimate: number }>(
        '/api/text/optimize-mail',
        {
          method: 'POST',
          body: {
            lead_id: lead.id,
            current_subject: subject,
            current_body: body,
          },
        },
      );
      setOptimizeBackup(backup);
      setSubject(resp.subject);
      setBody(resp.body);
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        try { navigator.vibrate(30); } catch { /* ignore */ }
      }
    } catch (err) {
      const e = err as { detail?: string; status?: number };
      if (e.status === 402) {
        setOptimizeError(
          'KI-Budget für diesen Monat aufgebraucht. Manueller Edit weiterhin moeglich.',
        );
      } else {
        setOptimizeError(e.detail ?? 'KI-Optimierung fehlgeschlagen');
      }
    } finally {
      setOptimizing(false);
    }
  }

  // Sprint 8 (S8.3): Template-Picker
  const [templates, setTemplates] = useState<MailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  // Sprint 80 · Mail-Anhaenge (Template + Ad-hoc-Draft)
  type MailAttachmentLite = {
    id: string;
    filename: string;
    mime: string;
    size_bytes: number;
  };
  const [templateAttachments, setTemplateAttachments] = useState<MailAttachmentLite[]>([]);
  const [draftAttachments, setDraftAttachments] = useState<MailAttachmentLite[]>([]);
  const [attachUploading, setMailAttachUploading] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const mailAttachInputRef = useRef<HTMLInputElement | null>(null);

  // Draft-Anhaenge des Leads laden (einmal beim Mount)
  useEffect(() => {
    let cancelled = false;
    api<MailAttachmentLite[]>(`/api/leads/${lead.id}/mail-attachments`)
      .then((list) => { if (!cancelled) setDraftAttachments(list); })
      .catch(() => { /* leere Liste */ });
    return () => { cancelled = true; };
  }, [lead.id]);

  // Template-Anhaenge bei Template-Wechsel neu laden
  useEffect(() => {
    if (!selectedTemplateId) { setTemplateAttachments([]); return; }
    let cancelled = false;
    api<MailAttachmentLite[]>(`/api/mail-templates/${selectedTemplateId}/attachments`)
      .then((list) => { if (!cancelled) setTemplateAttachments(list); })
      .catch(() => { if (!cancelled) setTemplateAttachments([]); });
    return () => { cancelled = true; };
  }, [selectedTemplateId]);

  const totalAttachBytes =
    templateAttachments.reduce((s, a) => s + a.size_bytes, 0) +
    draftAttachments.reduce((s, a) => s + a.size_bytes, 0);
  const SEND_LIMIT = 4 * 1024 * 1024;

  async function uploadDraftAttachments(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setMailAttachUploading(true);
    setAttachError(null);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        const resp = await fetch(`/api/leads/${lead.id}/mail-attachments`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
          body: form,
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.detail ?? `Upload fehlgeschlagen (${resp.status})`);
        }
        const att = (await resp.json()) as MailAttachmentLite;
        setDraftAttachments((prev) => [...prev, att]);
      }
    } catch (err) {
      setAttachError((err as Error).message);
    } finally {
      setMailAttachUploading(false);
      if (mailAttachInputRef.current) mailAttachInputRef.current.value = '';
    }
  }

  async function deleteDraftAttachment(attId: string): Promise<void> {
    try {
      await api(`/api/leads/${lead.id}/mail-attachments/${attId}`, { method: 'DELETE' });
      setDraftAttachments((prev) => prev.filter((a) => a.id !== attId));
    } catch (err) {
      setAttachError((err as { detail?: string }).detail ?? 'Loeschen fehlgeschlagen');
    }
  }

  function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await api<MailTemplate[]>('/api/mail-templates');
        if (cancelled) return;
        setTemplates(list);
        const def = list.find((t) => t.is_default) ?? list[0];
        if (def) {
          setSelectedTemplateId(def.id);
          // Sprint 77 · NICHT ueberschreiben wenn bereits ein Versandbereit-Draft
          // existiert — sonst sieht der Reviewer den Default statt der vorbereiteten
          // Mail (Andy-Bug). Template-Picker ist weiterhin manuell erreichbar.
          if (!hasDraft) {
            setSubject(applyVariables(def.subject, templateVars));
            setBody(applyVariables(def.body_text, templateVars));
          }
        }
      } catch {
        // Picker bleibt leer — Fallback-Subject/Body sind schon gesetzt
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [firstName, hasDraft]);

  function applyTemplate(id: string): void {
    const tmpl = templates.find((t) => t.id === id);
    if (!tmpl) return;
    // Sprint 77 · Bei vorhandenem Versandbereit-Draft 2x bestaetigen lassen,
    // bevor der vorbereitete Text durch ein Template ueberschrieben wird.
    if (hasDraft) {
      const ok1 = window.confirm(
        'Es gibt bereits einen vorbereiteten Entwurf (Versandbereit). Template wirklich anwenden und den Entwurf ueberschreiben?',
      );
      if (!ok1) return;
      const ok2 = window.confirm(
        'Sicher? Der vorbereitete Text wird ersetzt und ist danach nicht mehr wiederherstellbar.',
      );
      if (!ok2) return;
    }
    setSelectedTemplateId(id);
    setSubject(applyVariables(tmpl.subject, templateVars));
    setBody(applyVariables(tmpl.body_text, templateVars));
  }

  // Sprint 77 · Aenderungen am Versandbereit-Entwurf?
  const draftDirty = Boolean(
    hasDraft &&
      existingDraft &&
      (subject !== (existingDraft.subject ?? '') || body !== (existingDraft.body ?? '')),
  );

  // Sprint 77 · Beim Schliessen warnen wenn ungespeicherte Aenderungen vorhanden
  function handleClose(): void {
    if (draftDirty) {
      const ok = window.confirm(
        'Du hast Aenderungen am vorbereiteten Entwurf gemacht, ohne sie zu speichern oder zu senden. Wirklich schliessen und Aenderungen verwerfen?',
      );
      if (!ok) return;
    }
    onClose();
  }

  // Sprint 77 · Admin/Dispatcher kann eigene Aenderungen am Erfasser-Draft
  // als neuen Entwurf speichern, ohne zu senden.
  async function saveDraftUpdate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/leads/${lead.id}/mail-draft`, {
        method: 'PUT',
        body: { subject, body },
      });
      await onSent();
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    setBusy(true);
    setError(null);
    try {
      if (preparerOnly) {
        // Sprint 73 · Erfasser-Modus: Mail nur als Draft speichern, Status auf 'geprueft'
        await api(`/api/leads/${lead.id}/mail-draft`, {
          method: 'PUT',
          body: { subject, body },
        });
        await onSent();
        return;
      }
      // Sprint 6 S6.2: Custom-Subject/Body wird jetzt an Backend durchgereicht
      // Leeres Subject/Body → Backend nutzt Default-Template.
      // Sprint 80 · template_id mit-senden damit Backend Template-Anhaenge findet
      const isCustom = subject !== FALLBACK_SUBJECT || body.trim() !== '';
      const sendBody: Record<string, unknown> = isCustom
        ? { subject, body_text: body }
        : {};
      if (selectedTemplateId) sendBody.template_id = selectedTemplateId;
      const result = await api<{ stub?: boolean; sent?: boolean }>(
        `/api/leads/${lead.id}/send-mail`,
        {
          method: 'POST',
          body: sendBody,
        },
      );
      if (result.stub) {
        // stub-mode: zeig hinweis aber gilt als gesendet im UI
      }
      await onSent();
    } catch (err) {
      const e = err as { detail?: string; status?: number };
      if (e.status === 409) {
        setError('Bereits versendet.');
      } else if (e.status === 502) {
        setError('Microsoft-Graph nicht verfügbar. Stub-Mode aktiv?');
      } else {
        setError(e.detail ?? 'Senden fehlgeschlagen');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-card mail-send-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mail-send-modal__head">
          <h2 className="app-shell__card-title">
            {preparerOnly ? 'Antwort-Mail vorbereiten' : 'Antwort-Mail senden'}
          </h2>
          <ol className="mail-send-stepper" aria-label="Phase">
            <li className={phase === 'edit' ? 'mail-send-stepper__step--active' : 'mail-send-stepper__step--done'}>
              1 · BEARBEITEN
            </li>
            <li className={phase === 'preview' ? 'mail-send-stepper__step--active' : ''}>
              2 · VORSCHAU + {preparerOnly ? 'VERSANDBEREIT' : 'SENDEN'}
            </li>
          </ol>
        </div>
        {hasDraft && existingDraft?.prepared_at ? (
          <div className="mail-draft-banner">
            ✎ Diese Mail wurde vorbereitet am{' '}
            {new Date(existingDraft.prepared_at).toLocaleString('de-DE')}.
            {' '}Du kannst zurück zum Bearbeiten oder direkt {preparerOnly ? 'als versandbereit markieren' : 'senden'}.
          </div>
        ) : null}

        <dl className="lead-detail-fields">
          <Field label="An" value={lead.email} mono />
          <Field label="Firma" value={lead.firma} />
        </dl>

        {phase === 'edit' ? (
          <>
            {templates.length > 0 ? (
              <div className="auth-field">
                <label className="auth-field__label" htmlFor="m-template">VORLAGE</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                  <select
                    id="m-template"
                    className="auth-input"
                    value={selectedTemplateId}
                    onChange={(e) => applyTemplate(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}{t.is_default ? ' · Default' : ''}
                      </option>
                    ))}
                  </select>
                  {/* Sprint 53 · Re-Apply-Button — manueller Edit/Wechsel-Issue
                       (User berichtete: Wechsel zwischen Vorlagen wendet die
                       Vorlage nicht zuverlaessig wieder an, vor allem nach
                       manuellem Bearbeiten) */}
                  <button
                    type="button"
                    className="app-shell__logout"
                    onClick={() => applyTemplate(selectedTemplateId)}
                    title="Vorlage Subject + Body neu anwenden (verwirft manuelle Bearbeitung)"
                  >
                    NEU ANWENDEN
                  </button>
                </div>
              </div>
            ) : null}

            <div className="auth-field">
              <label className="auth-field__label" htmlFor="m-subject">BETREFF</label>
              <input
                id="m-subject"
                className="auth-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="auth-field">
              <div className="lead-detail-section-header" style={{ alignItems: 'baseline' }}>
                <label className="auth-field__label" htmlFor="m-body" style={{ margin: 0 }}>
                  BODY (Template)
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {/* Sprint 57 (Feature #5) · MAGIC-Optimize-Button.
                       Sprint 59 · INLINE-VORSCHAU-Toggle entfernt (war
                       verwirrend, gleicher Inhalt nur readonly vs editable). */}
                  <button
                    type="button"
                    className="app-shell__logout"
                    onClick={() => void runMailOptimize()}
                    disabled={optimizing}
                    style={{ padding: '4px 10px', fontSize: 11 }}
                    title="Mail mit KI optimieren — bezieht Notiz, Übernehmenden, Branche, Funktion + Messetag mit ein"
                  >
                    <Sparkles size={11} aria-hidden="true" style={{ marginRight: 4 }} />
                    {optimizing ? 'OPTIMIERT…' : 'KI OPTIMIEREN'}
                  </button>
                </div>
              </div>
              {optimizeError ? (
                <p className="auth-error" role="alert" style={{ marginBottom: 8 }}>
                  {optimizeError}
                </p>
              ) : null}
              {optimizeBackup ? (
                <p className="form-field__hint" style={{ marginBottom: 8 }}>
                  KI-optimierte Version aktiv. {' '}
                  <button
                    type="button"
                    className="app-shell__logout"
                    onClick={() => {
                      setSubject(optimizeBackup.subject);
                      setBody(optimizeBackup.body);
                      setOptimizeBackup(null);
                    }}
                    style={{ padding: '2px 8px', fontSize: 10, marginLeft: 4 }}
                  >
                    RÜCKGÄNGIG
                  </button>
                </p>
              ) : null}
              {/* Sprint 59 · immer textarea (kein toggle) */}
              <textarea
                id="m-body"
                className="auth-input form-field__textarea"
                rows={12}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>

            <p className="form-field__hint">
              Tenant-Footer wird automatisch angehängt. Vorlagen können unter{' '}
              <strong>Einstellungen · Mail-Templates</strong> verwaltet werden
              (inkl. Variablen-Hilfe).
            </p>

            {/* Sprint 80 · Mail-Anhaenge (Template + Ad-hoc-Draft) */}
            <div className="mail-attach-section">
              <div className="mail-attach-section__head">
                <strong>Anhänge</strong>
                <span className="mail-attach-section__size">
                  {formatBytes(totalAttachBytes)} / {formatBytes(SEND_LIMIT)}
                  {totalAttachBytes > SEND_LIMIT ? ' · ÜBER LIMIT' : ''}
                </span>
              </div>
              {templateAttachments.length > 0 ? (
                <ul className="mail-attach-list">
                  {templateAttachments.map((a) => (
                    <li key={a.id} className="mail-attach-item mail-attach-item--template">
                      <span className="mail-attach-item__name">{a.filename}</span>
                      <span className="mail-attach-item__size">{formatBytes(a.size_bytes)}</span>
                      <span className="mail-attach-item__tag">aus Vorlage</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {draftAttachments.length > 0 ? (
                <ul className="mail-attach-list">
                  {draftAttachments.map((a) => (
                    <li key={a.id} className="mail-attach-item">
                      <span className="mail-attach-item__name">{a.filename}</span>
                      <span className="mail-attach-item__size">{formatBytes(a.size_bytes)}</span>
                      <button
                        type="button"
                        className="mail-attach-item__delete"
                        onClick={() => void deleteDraftAttachment(a.id)}
                        aria-label={`${a.filename} entfernen`}
                      >
                        ENTFERNEN
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <input
                ref={mailAttachInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.csv,.zip"
                onChange={(e) => void uploadDraftAttachments(e)}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="app-shell__logout"
                onClick={() => mailAttachInputRef.current?.click()}
                disabled={attachUploading}
              >
                {attachUploading ? 'LADE HOCH…' : '+ ANHANG HINZUFÜGEN'}
              </button>
              {attachError ? (
                <p className="auth-error" style={{ marginTop: 8 }}>{attachError}</p>
              ) : null}
              {totalAttachBytes > SEND_LIMIT ? (
                <p className="form-field__hint" style={{ color: 'var(--tfc-state-error)' }}>
                  Microsoft-Graph erlaubt max 4 MB pro Mail. Bitte größere Anhänge entfernen.
                </p>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <div className="mail-send-fullpreview">
              <div className="mail-send-fullpreview__head">
                <div>
                  <span className="mail-send-fullpreview__label">VON</span>
                  <span className="admin-table__mono">{fromEmail}</span>
                </div>
                <div>
                  <span className="mail-send-fullpreview__label">AN</span>
                  <span className="admin-table__mono">{lead.email}</span>
                </div>
                <div>
                  <span className="mail-send-fullpreview__label">BETREFF</span>
                  <strong>{subject}</strong>
                </div>
              </div>
              <div
                className="mail-send-fullpreview__body"
                dangerouslySetInnerHTML={{
                  __html: renderBodyForPreview(body),
                }}
              />
              {/* Sprint 80 · Anhaenge im Preview */}
              {templateAttachments.length + draftAttachments.length > 0 ? (
                <div className="mail-send-fullpreview__attachments">
                  <strong>Anhänge ({templateAttachments.length + draftAttachments.length})</strong>
                  <ul>
                    {[...templateAttachments, ...draftAttachments].map((a) => (
                      <li key={a.id}>
                        📎 {a.filename} · {formatBytes(a.size_bytes)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="mail-send-fullpreview__footer">
                Server fügt Tenant-Footer (Adresse / Impressum) automatisch unten an.
              </div>
            </div>
          </>
        )}

        {error ? <p className="auth-error" role="alert">{error}</p> : null}
        <div className="mail-send-modal__actions">
          {phase === 'edit' ? (
            <>
              <button
                type="button"
                className="auth-submit"
                onClick={() => setPhase('preview')}
                disabled={busy}
              >
                WEITER ZUR VORSCHAU →
              </button>
              <button type="button" className="app-shell__logout" onClick={handleClose} disabled={busy}>
                ABBRECHEN
              </button>
            </>
          ) : (
            <>
              <button type="button" className="auth-submit" onClick={send} disabled={busy}>
                {busy
                  ? (preparerOnly ? 'SPEICHERT…' : 'SENDET…')
                  : preparerOnly
                    ? '✓  ALS VERSANDBEREIT MARKIEREN'
                    : '✉  SENDEN BESTÄTIGEN'}
              </button>
              {/* Sprint 77 · Admin/Dispatcher kann seine Aenderungen am Entwurf
                  zwischenspeichern statt zu senden — Entwurf bleibt Versandbereit. */}
              {!preparerOnly && draftDirty ? (
                <button
                  type="button"
                  className="app-shell__logout"
                  onClick={() => void saveDraftUpdate()}
                  disabled={busy}
                >
                  💾 ENTWURF AKTUALISIEREN
                </button>
              ) : null}
              <button
                type="button"
                className="app-shell__logout"
                onClick={() => setPhase('edit')}
                disabled={busy}
              >
                ← ZURÜCK ZUM EDITOR
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function renderBodyForPreview(plainText: string): string {
  // Plain-Text → HTML mit Escape + <br>-Conversion · spiegelt Backend _wrap_html
  const escaped = plainText
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return escaped.replace(/\n/g, '<br>');
}

// Sprint 20 · S20.2 · Lead-Status-History Audit-Log Section
interface MailHistoryEntry {
  id: string;
  recipient_email: string;
  subject: string;
  body_html: string;
  sent_by_name: string | null;
  sent_at: string;
  send_mode: string;
}

function MailHistorySection({ leadId }: { leadId: string }) {
  const [entries, setEntries] = useState<MailHistoryEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<MailHistoryEntry[]>(`/api/leads/${leadId}/mail-history`)
      .then((data) => {
        if (!cancelled) setEntries(data);
      })
      .catch((err: { detail?: string }) => {
        if (!cancelled) setLoadError(err.detail ?? 'History nicht ladbar');
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  return (
    <div className="lead-detail-card-section">
      <h2 className="profile-section-title">Versendete Mails</h2>
      {loadError ? <p className="auth-error">{loadError}</p> : null}
      {entries === null && !loadError ? (
        <p className="form-field__hint">Lädt…</p>
      ) : null}
      {entries !== null && entries.length === 0 ? (
        <p className="status-history-empty">
          Noch keine Mails an diesen Lead versendet.
        </p>
      ) : null}
      {entries !== null && entries.length > 0 ? (
        <ul className="mail-history" aria-label="Versendete Mails">
          {entries.map((m) => {
            const isOpen = openId === m.id;
            return (
              <li key={m.id} className="mail-history-item">
                <button
                  type="button"
                  className="mail-history-item__toggle"
                  onClick={() => setOpenId(isOpen ? null : m.id)}
                  aria-expanded={isOpen}
                >
                  <div className="mail-history-item__row">
                    <span className="mail-history-item__subject">{m.subject || '(kein Betreff)'}</span>
                    <span className="mail-history-item__chevron" aria-hidden="true">
                      {isOpen ? '▾' : '▸'}
                    </span>
                  </div>
                  <div className="mail-history-item__meta">
                    <span className="admin-table__mono">{m.recipient_email}</span>
                    <span className="status-history-item__meta">
                      · {new Date(m.sent_at).toLocaleString('de-DE')}
                      {m.sent_by_name ? ` · von ${m.sent_by_name}` : ''}
                    </span>
                  </div>
                </button>
                {isOpen ? (
                  // Sprint 30 · Review-Iter-1 · iframe-sandbox statt
                  // dangerouslySetInnerHTML — neutralisiert XSS auch wenn
                  // body_html durch Phase-2-Bug oder DB-Manipulation
                  // unsicheres HTML enthält. `srcdoc` rendert das HTML in
                  // einem isolierten Origin ohne Bearer-Token-Zugriff.
                  <iframe
                    title={`Mail-Body ${m.subject}`}
                    sandbox=""
                    srcDoc={m.body_html}
                    className="mail-history-item__body"
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

interface StatusHistoryEntry {
  id: string;
  lead_id: string;
  tenant_id: string;
  from_status: string | null;
  to_status: string;
  changed_by: string | null;
  changed_by_name: string | null;
  changed_at: string;
}

interface NoteHistoryEntry {
  id: string;
  old_notiz: string | null;
  new_notiz: string | null;
  changed_at: string;
  changed_by_name: string | null;
}

// Sprint 74 · Status + Notiz in einer gemeinsamen Timeline.
type TimelineEntry =
  | {
      kind: 'status';
      id: string;
      changed_at: string;
      changed_by_name: string | null;
      from_status: string | null;
      to_status: string;
    }
  | {
      kind: 'note';
      id: string;
      changed_at: string;
      changed_by_name: string | null;
      old_notiz: string | null;
      new_notiz: string | null;
    };

function HistorySection({
  leadId,
  refreshKey,
}: {
  leadId: string;
  refreshKey: number;
}) {
  const [entries, setEntries] = useState<TimelineEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api<StatusHistoryEntry[]>(`/api/leads/${leadId}/status-history`),
      api<NoteHistoryEntry[]>(`/api/leads/${leadId}/note-history`).catch(
        () => [] as NoteHistoryEntry[],
      ),
    ])
      .then(([status, notes]) => {
        if (cancelled) return;
        const merged: TimelineEntry[] = [
          ...status.map<TimelineEntry>((s) => ({
            kind: 'status',
            id: `s-${s.id}`,
            changed_at: s.changed_at,
            changed_by_name: s.changed_by_name,
            from_status: s.from_status,
            to_status: s.to_status,
          })),
          ...notes.map<TimelineEntry>((n) => ({
            kind: 'note',
            id: `n-${n.id}`,
            changed_at: n.changed_at,
            changed_by_name: n.changed_by_name,
            old_notiz: n.old_notiz,
            new_notiz: n.new_notiz,
          })),
        ];
        merged.sort((a, b) => (a.changed_at < b.changed_at ? 1 : -1));
        setEntries(merged);
      })
      .catch((err: { detail?: string }) => {
        if (!cancelled) setLoadError(err.detail ?? 'Verlauf nicht ladbar');
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, refreshKey]);

  return (
    <div className="lead-detail-card-section">
      <h2 className="profile-section-title">Verlauf</h2>
      {loadError ? <p className="auth-error">{loadError}</p> : null}
      {entries === null && !loadError ? (
        <p className="form-field__hint">Lädt…</p>
      ) : null}
      {entries !== null && entries.length === 0 ? (
        <p className="status-history-empty">Noch keine Änderungen aufgezeichnet.</p>
      ) : null}
      {entries !== null && entries.length > 0 ? (
        <ul className="status-history" aria-label="Lead-Verlauf">
          {entries.map((e) =>
            e.kind === 'status' ? (
              <li key={e.id} className="status-history-item">
                <div className="status-history-item__row">
                  {e.from_status ? (
                    <span className={`status-pill status-pill--${e.from_status}`}>
                      {STATUS_LABELS[e.from_status] ?? e.from_status}
                    </span>
                  ) : (
                    <span className="status-history-item__meta">·</span>
                  )}
                  <span className="status-history-item__arrow" aria-hidden="true">
                    →
                  </span>
                  <span className={`status-pill status-pill--${e.to_status}`}>
                    {STATUS_LABELS[e.to_status] ?? e.to_status}
                  </span>
                </div>
                <div className="status-history-item__meta">
                  {new Date(e.changed_at).toLocaleString('de-DE')}
                  {e.changed_by_name ? ` · ${e.changed_by_name}` : ' · System'}
                </div>
              </li>
            ) : (
              <li key={e.id} className="status-history-item status-history-item--note">
                <div className="status-history-item__row">
                  <span className="history-kind-pill">NOTIZ</span>
                </div>
                <div className="status-history-item__meta">
                  {new Date(e.changed_at).toLocaleString('de-DE')}
                  {e.changed_by_name ? ` · ${e.changed_by_name}` : ' · System'}
                </div>
                <div className="note-history-item__block note-history-item__block--old">
                  <div className="note-history-item__label">VORHER</div>
                  <div
                    className={
                      e.old_notiz
                        ? 'note-history-item__body'
                        : 'note-history-item__body note-history-item__body--empty'
                    }
                  >
                    {e.old_notiz || '(leer)'}
                  </div>
                </div>
                <div className="note-history-item__block note-history-item__block--new">
                  <div className="note-history-item__label">NACHHER</div>
                  <div
                    className={
                      e.new_notiz
                        ? 'note-history-item__body'
                        : 'note-history-item__body note-history-item__body--empty'
                    }
                  >
                    {e.new_notiz || '(leer)'}
                  </div>
                </div>
              </li>
            ),
          )}
        </ul>
      ) : null}
    </div>
  );
}
